import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ShieldCheck, Building2, BadgeCheck, Users2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "./api";
import { useAuth } from "./AuthContext";

const ROLES = [
  { value: "hr_admin", label: "HR Admin" },
  { value: "finance", label: "Finance" },
  { value: "project_manager", label: "Project Manager" },
  { value: "employee", label: "Employee" },
];

function ListManager({ title, icon: Icon, endpoint, testid }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get(endpoint).then((r) => setItems(r.data.map((x) => x.name))).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post(endpoint, { name: name.trim() });
      setName("");
      load();
      toast.success(`${title.slice(0, -1)} added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (n) => {
    try {
      await api.delete(`${endpoint}/${encodeURIComponent(n)}`);
      load();
      toast.success(`${title.slice(0, -1)} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className="bg-[#0C1322] border border-white/10 p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4"><Icon size={18} style={{ color: "#00F0FF" }} /><h2 className="font-display font-bold text-lg text-white">{title}</h2></div>
      <form onSubmit={add} className="flex gap-2 mb-4">
        <input className="field" placeholder={`Add ${title.slice(0, -1).toLowerCase()}`} value={name} onChange={(e) => setName(e.target.value)} data-testid={`${testid}-input`} />
        <button type="submit" className="bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-4 transition-colors" data-testid={`${testid}-add`}><Plus size={18} /></button>
      </form>
      <div className="flex flex-wrap gap-2">
        {items.map((i) => (
          <span key={i} className="mono-label pl-3 pr-2 py-1.5 border border-white/10 text-slate-300 flex items-center gap-2">
            {i}
            <button onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} title="Remove"
              className="text-slate-500 hover:text-red-400 transition-colors">
              <X size={13} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function UserManager() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/create-user", form);
      toast.success("User account created");
      setForm({ name: "", email: "", password: "", role: "employee" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-[#0C1322] border border-white/10 p-6" data-testid="user-manager">
      <div className="flex items-center gap-2 mb-4"><Users2 size={18} style={{ color: "#00F0FF" }} /><h2 className="font-display font-bold text-lg text-white">User Accounts & Roles</h2></div>
      <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <input className="field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name" />
        <input type="email" className="field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email" />
        <input type="password" className="field" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password" />
        <select className="field appearance-none cursor-pointer" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <div className="sm:col-span-2 lg:col-span-4">
          <button type="submit" disabled={saving} data-testid="create-user-btn"
            className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={16} /> Create User</>}
          </button>
        </div>
      </form>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between py-2.5 border-b border-white/5 text-sm">
            <div><span className="text-white">{u.name}</span> <span style={{ color: "#64748B" }}>· {u.email}</span></div>
            <span className="mono-label px-2 py-1 border border-cyan/30" style={{ color: "#00F0FF" }}>{u.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  useEffect(() => { api.get("/hr/meta").then((r) => setCompany(r.data.company)).catch(() => {}); }, []);

  return (
    <div data-testid="settings-page">
      <span className="mono-label" style={{ color: "#00F0FF" }}>Admin Settings</span>
      <h1 className="mt-2 mb-6 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">Settings</h1>

      {company && (
        <div className="bg-[#0C1322] border border-white/10 p-6 mb-5" data-testid="company-info">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} style={{ color: "#00F0FF" }} /><h2 className="font-display font-bold text-lg text-white">Company Details</h2></div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span style={{ color: "#64748B" }}>Name: </span><span className="text-white">{company.name}</span></div>
            <div><span style={{ color: "#64748B" }}>GSTIN: </span><span className="text-white">{company.gst}</span></div>
            <div className="sm:col-span-2"><span style={{ color: "#64748B" }}>Address: </span><span className="text-white">{company.address}</span></div>
            <div><span style={{ color: "#64748B" }}>Email: </span><span className="text-white">{company.email}</span></div>
            <div><span style={{ color: "#64748B" }}>Phone: </span><span className="text-white">{company.phone}</span></div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <ListManager title="Departments" icon={Building2} endpoint="/hr/departments" testid="departments-manager" />
        <ListManager title="Designations" icon={BadgeCheck} endpoint="/hr/designations" testid="designations-manager" />
      </div>

      {["super_admin", "hr_admin"].includes(user?.role) && <UserManager />}
    </div>
  );
}
